import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { type Merchant } from '@trendiolive/trendio-selfcare-ui-kit';

import { MerchantSettings } from 'model/classes/MerchantSettings';
import { MerchantState } from 'model/types/merchant';
import { CreatorTier, CreatorTierConnectionResult, CreatorTierInput, CreatorTierSetCreateResult } from 'model/types/creator-tier';

import { GET_MERCHANT_BASIC_INFO } from 'queries/get_merchant_basic_info';
import { GET_CREATOR_TIERS } from 'queries/get_creator_tiers';
import { CREATOR_TIER_SET_CREATE } from 'queries/creator_tier_set_create';

import { authenticatedPost } from 'utils/axios';
import { extractFollowersTierFromMetadata, extractStarterTier, extractUnconnectedCreatorsTierRestriction } from 'utils/metadataParser';

export const initialState: MerchantState = {
  displayDashboard: true,
  displayMessageCenter: false,
  merchant: null
};

export const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    setBasicInfo(state, action: PayloadAction<MerchantState>) {
      state.merchant = action.payload.merchant;
      state.ttsPermissionsGranted = action.payload.merchant?.onboarding?.tiktokSellerPortalPermissionsGranted ?? false;
      state.displayMessageCenter = new MerchantSettings(action.payload.merchant?.settingsObj).getSettingsBooleanAtPath(
        MerchantSettings.PATHS.dm_auto_reply
      );
      state.tiktokPlan = action.payload.tiktokPlan;
      state.youtubePlan = action.payload.youtubePlan;
    },
    grantTTSPermissions(state, action: PayloadAction<MerchantState>) {
      state.ttsPermissionsGranted = action.payload.ttsPermissionsGranted;
    },
    setDisplayDashboard(state, action: PayloadAction<boolean>) {
      state.displayDashboard = action.payload;
    },
    setTTSDashboardUrl(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.selfcareDashboardUrl = action.payload.merchant?.selfcareDashboardUrl;
      }
    },
    setCurrentTTSBillingPeriod(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.settingsObj = action.payload.merchant?.settingsObj;
        state.ttsTierRestrictions = handleMerchantTierRestrictions(action.payload.merchant?.metadata);
        if (state.merchant.onboarding) state.merchant.onboarding = { ...state.merchant.onboarding, ...action.payload.merchant?.onboarding };
        if (state.tiktokPlan) state.tiktokPlan = { ...state.tiktokPlan, ...action.payload.merchant?.plan };
      }
    },
    setGoogleAuthUrl(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.googleAuthUrl = action.payload.merchant?.googleAuthUrl;
      }
    },
    setMerchantSettingsObj(state, action: PayloadAction<Merchant['settingsObj']>) {
      if (state.merchant !== null && action.payload != null) {
        state.merchant.settingsObj = action.payload;
        state.displayMessageCenter = new MerchantSettings(action.payload).getSettingsBooleanAtPath(MerchantSettings.PATHS.dm_auto_reply);
      }
    },
    setCreatorTiers(state, action: PayloadAction<CreatorTier[]>) {
      state.creatorTiers = action.payload;
    }
  }
});

export const {
  grantTTSPermissions,
  setBasicInfo,
  setDisplayDashboard,
  setTTSDashboardUrl,
  setCurrentTTSBillingPeriod,
  setGoogleAuthUrl,
  setMerchantSettingsObj,
  setCreatorTiers
} = merchantSlice.actions;

const handleMerchantTierRestrictions = (metadata?: string) => {
  if (metadata) {
    const followerRestrictions = extractFollowersTierFromMetadata(metadata);
    const unconnectedCreators = extractUnconnectedCreatorsTierRestriction(metadata);
    const isStarter = extractStarterTier(metadata);
    return { followers: followerRestrictions, unconnectedCreators, isStarter };
  }
};

// use cases
export async function queryMerchantInfo(): Promise<MerchantState> {
  const result = await authenticatedPost({ query: GET_MERCHANT_BASIC_INFO });
  if (result.data.merchant) {
    const state: MerchantState = {
      merchant: result.data.merchant,
      tiktokPlan: result.data.merchant.tiktokPlan,
      youtubePlan: result.data.merchant.youtubePlan
    };
    return state;
  }
  return { merchant: null };
}

export async function queryCreatorTiers(): Promise<CreatorTier[]> {
  const result = await authenticatedPost({ query: GET_CREATOR_TIERS });
  const data = result.data as CreatorTierConnectionResult;
  return data.creatorTiers?.edges.map((edge) => edge.node) ?? [];
}

export async function saveCreatorTierSet(tiers: CreatorTierInput[]): Promise<CreatorTier[]> {
  const result = await authenticatedPost({
    query: CREATOR_TIER_SET_CREATE,
    vars: { input: { tiers } }
  });
  const data = result.data as CreatorTierSetCreateResult;
  return data.creatorTierSetCreate?.creatorTiers ?? [];
}
